import React, { useCallback, useEffect, useRef, useState } from 'react';
import { currentUiZoom } from '../../utils/uiScale';
import { RESIZE_STEP_LARGE_PX, RESIZE_STEP_PX } from './paneLayout';

/**
 * 两个区域之间的竖向拖拽手柄。
 *
 * `direction` 把"屏幕向右"映射为该区域的宽度增量：
 * - 左侧栏的手柄 → `direction = 1`（往右拖变宽）
 * - 右侧面板的手柄（手柄在其**左**边缘）→ `direction = -1`（往左拖变宽）
 *
 * 键盘与鼠标共用这一套映射：→ 键永远表示"把分界线往右移"，
 * 因此左侧栏按 → 变宽、右侧面板按 → 变窄，与视觉一致。
 *
 * 无障碍：这是一个 window splitter，按 ARIA 规范给 `role="separator"` +
 * `aria-orientation` + `aria-valuenow`，可 Tab 聚焦并用方向键调整。
 * `aria-valuemax` 只用于给读屏一个量程，**不是布局上限**（宽度本身不设上限）。
 */
const A11Y_VALUE_MAX = 4000;

interface ResizeHandleProps {
  direction: 1 | -1;
  /** 当前该区域宽度（用于 aria-valuenow） */
  value: number;
  /** 该区域的最小宽度（用于 aria-valuemin） */
  min: number;
  /** 参数是"该区域宽度的增量"（已按 direction 换算好） */
  onResize: (deltaWidthPx: number) => void;
  /** 双击/键盘 Home 复位该区域宽度 */
  onReset?: () => void;
  label: string;
  className?: string;
}

export function ResizeHandle({
  direction, value, min, onResize, onReset, label, className,
}: ResizeHandleProps) {
  const lastX = useRef<number | null>(null);
  const scaleRef = useRef(1);
  const [dragging, setDragging] = useState(false);

  // 拖拽期间必须关掉面板的 190ms 宽度过渡，否则指针已经移过去了、边框还在追，
  // 手感会明显“慢半拍”。类挂在 body 上，同一时刻只有一个手柄在拖。
  useEffect(() => () => document.body.classList.remove('is-resizing-pane'), []);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    // 只响应主键：右键/中键拖拽会留下卡住的捕获状态
    if (e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    document.body.classList.add('is-resizing-pane');
    // 不要只信模块级缓存：窗口缩放/浏览器缩放/桌面端窗口迁移都可能让缓存落后。
    // 用 #root 的视觉宽度 ÷ 布局宽度得到**本次拖拽真实生效**的缩放比。
    const root = document.getElementById('root');
    const rect = root?.getBoundingClientRect();
    const layoutWidth = root?.offsetWidth ?? 0;
    scaleRef.current = rect && rect.width > 0 && layoutWidth > 0
      ? rect.width / layoutWidth
      : (currentUiZoom() || 1);
    lastX.current = e.clientX;
    setDragging(true);
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const prev = lastX.current;
    if (prev === null) return;
    const dx = e.clientX - prev;
    if (dx === 0) return;       // 同一像素上的重复事件不必触发 setState
    lastX.current = e.clientX;
    /**
     * ⚠️ 指针坐标是**视觉像素**，而 `value/onResize` 是**布局像素** —— 界面整体缩放
     * （`#root` 的 zoom，见 utils/uiScale.ts）之后两者差一个系数：zoom 0.8 时指针移动
     * 100px 只对应布局里的 125px。不换算的话拖拽手感会随窗口大小变化（小窗口里"跑得飞快"）。
     */
    onResize((dx / (scaleRef.current || 1)) * direction);
  }, [direction, onResize]);

  const endDrag = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (lastX.current === null) return;
    lastX.current = null;
    setDragging(false);
    document.body.classList.remove('is-resizing-pane');
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // 指针已被释放（例如 pointercancel 之后）—— 无需处理
    }
  }, []);

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      const step = e.shiftKey ? RESIZE_STEP_LARGE_PX : RESIZE_STEP_PX;
      // 屏幕方向 → 宽度增量，与鼠标同一套换算
      onResize((e.key === 'ArrowRight' ? step : -step) * direction);
      return;
    }
    if (e.key === 'Home' && onReset) {
      e.preventDefault();
      onReset();
    }
  }, [direction, onResize, onReset]);

  return (
    <div
      className={`pane-resize-handle${dragging ? ' is-dragging' : ''}${className ? ` ${className}` : ''}`}
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={Math.round(value)}
      aria-valuemin={min}
      aria-valuemax={A11Y_VALUE_MAX}
      tabIndex={0}
      title={`拖动调整宽度（双击或按 Home 恢复默认）：${label}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onLostPointerCapture={endDrag}
      onKeyDown={onKeyDown}
      onDoubleClick={onReset}
    />
  );
}
